import { useState, useEffect, useCallback } from 'react';
import { Plus, Loader2, FileX2, X, Trash2, LayoutGrid } from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR, formatDate } from '@/utils/formatters';
import { ProductGridSelector } from '@/components/shared/ProductGridSelector';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CreditNote {
  id: string;
  number: string;
  date?: string;
  createdAt?: string;
  invoiceRef?: string;
  invoiceNumber?: string;
  invoiceId?: string;
  invoice?: { number: string } | null;
  contactName?: string;
  contactId?: string;
  contact?: { firstName: string; lastName: string } | null;
  amount?: number;
  subtotal?: number;
  total?: number;
  status: string;
  items?: any[];
}

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
}

interface Invoice {
  id: string;
  number: string;
}

interface LineItem {
  description: string;
  hsnSac: string;
  quantity: number;
  rate: number;
  gstRate: number;
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600',
  issued: 'bg-blue-50 text-blue-700',
  applied: 'bg-green-50 text-green-700',
  void: 'bg-red-50 text-red-700',
  cancelled: 'bg-red-50 text-red-700',
};

const CN_STATUSES = ['draft', 'issued', 'cancelled'];

const GST_RATES = [0, 5, 12, 18, 28];

const emptyItem = (): LineItem => ({ description: '', hsnSac: '', quantity: 1, rate: 0, gstRate: 18 });

// ─── Main Component ───────────────────────────────────────────────────────────

export function CreditNotesPage() {
  const [creditNotes, setCreditNotes] = useState<CreditNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create modal
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [form, setForm] = useState({
    reason: '',
    contactId: '',
    invoiceId: '',
    buyerGstin: '',
  });
  const [items, setItems] = useState<LineItem[]>([emptyItem()]);
  const [showGrid, setShowGrid] = useState(false);

  const fetchCreditNotes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/credit-notes');
      setCreditNotes(res.data.data?.creditNotes ?? res.data.data ?? []);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setCreditNotes([]);
        setError(null);
      } else {
        setError(err.response?.data?.error || 'Failed to load credit notes');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCreditNotes(); }, [fetchCreditNotes]);

  const openCreateModal = () => {
    api.get('/contacts').then(res => setContacts(res.data.data?.contacts ?? res.data.data ?? [])).catch(() => {});
    api.get('/invoices').then(res => setInvoices(res.data.data?.invoices ?? res.data.data ?? [])).catch(() => {});
    setForm({ reason: '', contactId: '', invoiceId: '', buyerGstin: '' });
    setItems([emptyItem()]);
    setShowModal(true);
  };

  const updateItem = (idx: number, field: keyof LineItem, value: string | number) => {
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const removeItem = (idx: number) => {
    if (items.length <= 1) return;
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  const handleProductSelect = (product: any) => {
    setItems(prev => [...prev, {
      description: product.name,
      hsnSac: product.hsnCode || '',
      quantity: 1,
      rate: Number(product.unitPrice),
      gstRate: Number(product.gstRate) || 18,
    }]);
  };

  const computeTotal = () => {
    return items.reduce((sum, item) => {
      const lineAmount = item.quantity * item.rate;
      const gst = lineAmount * (item.gstRate / 100);
      return sum + lineAmount + gst;
    }, 0);
  };

  const handleCreate = async () => {
    try {
      setSaving(true);
      await api.post('/credit-notes', {
        reason: form.reason,
        contactId: form.contactId || undefined,
        invoiceId: form.invoiceId || undefined,
        buyerGstin: form.buyerGstin || undefined,
        items: items.map(i => ({
          description: i.description,
          hsnSac: i.hsnSac || undefined,
          quantity: i.quantity,
          rate: i.rate,
          gstRate: i.gstRate,
        })),
      });
      setShowModal(false);
      fetchCreditNotes();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create credit note');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Credit Notes</h1>
          <p className="text-sm text-gray-500">Manage credit notes and adjustments</p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] transition-colors"
        >
          <Plus size={16} />
          Create Credit Note
        </button>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
            <span className="ml-2 text-sm text-gray-500">Loading credit notes...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 text-red-500">
            <p className="text-sm">{error}</p>
            <button onClick={fetchCreditNotes} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Retry</button>
          </div>
        ) : creditNotes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <FileX2 size={40} className="mb-2" />
            <p className="text-sm">No credit notes found</p>
            <p className="text-xs">Credit notes will appear here when created</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3">CN #</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Invoice Ref</th>
                  <th className="px-4 py-3">Contact</th>
                  <th className="px-4 py-3 text-right">Amount</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-center w-16">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {creditNotes.map((cn) => (
                  <tr key={cn.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-[var(--ink-0)]">{cn.number || '-'}</td>
                    <td className="px-4 py-3 text-gray-600">{cn.date || cn.createdAt ? formatDate(cn.date || cn.createdAt!) : '-'}</td>
                    <td className="px-4 py-3 text-gray-600">{cn.invoiceRef || cn.invoiceNumber || cn.invoice?.number || '-'}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {cn.contactName || (cn.contact ? `${cn.contact.firstName} ${cn.contact.lastName}` : '-')}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">{formatINR(cn.total ?? cn.subtotal ?? cn.amount ?? 0)}</td>
                    <td className="px-4 py-3">
                      <select
                        value={cn.status || 'draft'}
                        onChange={async (e) => {
                          try {
                            await api.patch(`/credit-notes/${cn.id}/status`, { status: e.target.value });
                            fetchCreditNotes();
                          } catch { alert('Failed to update status'); }
                        }}
                        className={`rounded-full border-0 px-2 py-0.5 text-xs font-medium cursor-pointer focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)] ${STATUS_COLORS[cn.status] || STATUS_COLORS.draft}`}
                      >
                        {CN_STATUSES.map(s => (
                          <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => {
                          if (window.confirm(`Delete credit note ${cn.number}?`)) {
                            api.delete(`/credit-notes/${cn.id}`).then(() => fetchCreditNotes()).catch(() => alert('Failed to delete'));
                          }
                        }}
                        className="inline-flex items-center justify-center rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Credit Note Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowModal(false)}>
          <div className="mx-4 w-full max-w-2xl rounded-xl bg-white shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 bg-white rounded-t-xl flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">Create Credit Note</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Reason *</label>
                <input type="text" value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} placeholder="Reason for credit note"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Contact</label>
                  <select value={form.contactId} onChange={e => setForm({ ...form, contactId: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                    <option value="">None</option>
                    {contacts.map(c => <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Invoice Reference</label>
                  <select value={form.invoiceId} onChange={e => setForm({ ...form, invoiceId: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                    <option value="">None</option>
                    {invoices.map(inv => <option key={inv.id} value={inv.id}>{inv.number}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Buyer GSTIN</label>
                <input type="text" value={form.buyerGstin} onChange={e => setForm({ ...form, buyerGstin: e.target.value })} placeholder="22AAAAA0000A1Z5"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
              </div>
              {/* Line Items */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Line Items *</label>
                  <div className="flex gap-2">
                    <button onClick={() => setShowGrid(true)} className="flex items-center gap-1 text-xs font-medium text-[var(--ink-0)] hover:underline"><LayoutGrid size={12} /> Browse Products</button>
                    <button onClick={() => setItems([...items, emptyItem()])} className="text-xs text-[var(--ink-0)] hover:underline">+ Add Item</button>
                  </div>
                </div>
                <div className="space-y-3">
                  {items.map((item, idx) => (
                    <div key={idx} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <div className="mb-2 flex items-start gap-2">
                        <input type="text" value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} placeholder="Description *"
                          className="flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none" />
                        <input type="text" value={item.hsnSac} onChange={e => updateItem(idx, 'hsnSac', e.target.value)} placeholder="HSN/SAC"
                          className="w-24 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none" />
                        {items.length > 1 && (
                          <button onClick={() => removeItem(idx)} className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"><Trash2 size={14} /></button>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="mb-0.5 block text-xs text-gray-500">Qty</label>
                          <input type="number" value={item.quantity} onChange={e => updateItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                            className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none" />
                        </div>
                        <div>
                          <label className="mb-0.5 block text-xs text-gray-500">Rate</label>
                          <input type="number" value={item.rate} onChange={e => updateItem(idx, 'rate', parseFloat(e.target.value) || 0)}
                            className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none" />
                        </div>
                        <div>
                          <label className="mb-0.5 block text-xs text-gray-500">GST %</label>
                          <select value={item.gstRate} onChange={e => updateItem(idx, 'gstRate', parseInt(e.target.value))}
                            className="w-full rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                            {GST_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
                          </select>
                        </div>
                      </div>
                      <p className="mt-1 text-right text-xs text-gray-500">Line total: {formatINR(item.quantity * item.rate * (1 + item.gstRate / 100))}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-right text-sm font-semibold text-gray-900">Grand Total: {formatINR(computeTotal())}</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button onClick={() => setShowModal(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={handleCreate} disabled={saving || !form.reason || items.length === 0 || !items[0].description}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
                {saving && <Loader2 size={14} className="animate-spin" />}
                Create Credit Note
              </button>
            </div>
          </div>
        </div>
      )}

      <ProductGridSelector open={showGrid} onClose={() => setShowGrid(false)} onSelect={handleProductSelect} />
    </div>
  );
}
